import type { Guide, GuideBlock } from './types.js';
import { Alert, Card } from '../components/ui.js';

function Block({ block }: { block: GuideBlock }) {
  switch (block.kind) {
    case 'paragraph':
      return <p className="text-sm leading-6 text-slate-700">{block.text}</p>;

    case 'bullets':
      return (
        <ul className="list-disc space-y-1.5 pl-5 text-sm leading-6 text-slate-700">
          {block.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      );

    case 'steps':
      return (
        <ol className="list-decimal space-y-1.5 pl-5 text-sm leading-6 text-slate-700">
          {block.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ol>
      );

    case 'definitions':
      return (
        <dl className="space-y-3">
          {block.items.map((item) => (
            <div key={item.term}>
              <dt className="text-sm font-semibold text-slate-900">{item.term}</dt>
              <dd className="text-sm leading-6 text-slate-700">{item.description}</dd>
            </div>
          ))}
        </dl>
      );

    case 'table':
      return (
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse text-left text-sm">
            {block.caption && (
              <caption className="pb-2 text-left text-xs text-slate-500">{block.caption}</caption>
            )}
            <thead>
              <tr className="border-b border-slate-300">
                {block.headers.map((header) => (
                  <th key={header} className="py-2 pr-4 align-bottom font-semibold text-slate-900">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row) => (
                <tr key={row.join('|')} className="border-b border-slate-200 last:border-0">
                  {row.map((cell, column) => (
                    <td
                      key={`${block.headers[column] ?? column}`}
                      className="py-2 pr-4 align-top leading-6 text-slate-700"
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case 'note':
      return (
        <Alert variant={block.variant === 'warning' ? 'warning' : 'info'} title={block.title}>
          {block.text}
        </Alert>
      );

    case 'procedure':
      return (
        <ol className="space-y-4">
          {block.steps.map((step, index) => (
            <li key={step.do} className="flex gap-3">
              <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-brand-50 text-xs font-semibold text-brand-800">
                {(block.startAt ?? 1) + index}
              </span>
              <div className="min-w-0 space-y-1">
                <p className="text-sm font-semibold leading-6 text-slate-900">{step.do}</p>
                <p className="text-sm leading-6 text-slate-600">
                  <span className="font-medium text-slate-500">Where: </span>
                  {step.where}
                </p>
                <p className="text-sm leading-6 text-slate-600">
                  <span className="font-medium text-slate-500">Then: </span>
                  {step.then}
                </p>
              </div>
            </li>
          ))}
        </ol>
      );
  }
}

/** Renders a guide with a contents list; used for both in-app guides and the workflow document. */
export function GuideView({ guide }: { guide: Guide }) {
  return (
    <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:gap-8">
      <nav aria-label="Contents" className="shrink-0 lg:sticky lg:top-6 lg:w-64 print:hidden">
        <Card title="Contents" bodyClassName="px-0 py-2 sm:px-0">
          <ul className="text-sm">
            {guide.sections.map((section) => (
              <li key={section.id}>
                <a
                  href={`#${section.id}`}
                  className="block px-4 py-1.5 text-slate-600 hover:bg-slate-50 hover:text-brand-800"
                >
                  {section.title}
                </a>
              </li>
            ))}
          </ul>
        </Card>
      </nav>

      <div className="min-w-0 flex-1 space-y-6">
        {guide.video && (
          <Card title={guide.video.title} className="print:hidden">
            <p className="mb-3 text-sm text-slate-700">{guide.video.description}</p>
            <video
              controls
              preload="metadata"
              className="w-full rounded-md border border-slate-200 bg-black"
              src={guide.video.src}
            >
              <track kind="captions" />
            </video>
            <a
              href={guide.video.src}
              download
              className="mt-2 inline-block text-sm font-medium text-brand-700 underline"
            >
              Download the video (MP4)
            </a>
          </Card>
        )}
        {guide.sections.map((section) => (
          <section key={section.id} id={section.id} className="scroll-mt-6">
            <Card title={section.title}>
              <div className="space-y-4">
                {section.blocks.map((block, index) => (
                  <Block key={index} block={block} />
                ))}
              </div>
            </Card>
          </section>
        ))}
      </div>
    </div>
  );
}
